import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import '../assets/variables.css';
import '../assets/clients.css';
import '../assets/panels.css';
import styles from '../assets/clientShow.module.css';
import Loader from '../components/Loader';
import AddClientContactModal from '../components/clients/AddClientContactModal';
import ManageClientContactsModal from '../components/clients/ManageClientContactsModal';
import { useAuth } from '../contexts/AuthContext';
import { expendituresService } from '../services/expendituresService';

// Local (not UTC) yyyy-mm-dd, so <input type="date"> never shifts a day
const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const gbpFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatMoney = (value) => gbpFormatter.format(Number(value) || 0);

const defaultExpenditureRange = () => ({
  date_from: `${new Date().getFullYear()}-01-01`,
  date_to: toIsoDate(new Date()),
});


// Data-protection restriction: only these users may export company contacts.
const CONTACTS_EXPORT_ALLOWED_EMAILS = [
  'stuart.woolgar+sm@global-guardians.co.uk',
  'lauren.boswell@global-guardians.co.uk',
  'vikaila.vekaria@global-guardians.co.uk',
];

const ClientShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canExportContacts =
    !!user?.email &&
    (CONTACTS_EXPORT_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase()) || user.email.trim().toLowerCase().endsWith('@kimonservices.com'));

  // Expenditure summary is internal-only - never shown to CLIENT users
  const roleName = user?.role?.name || user?.role;
  const isClientUser = roleName === 'CLIENT';

  const [client, setClient] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [monthlyReports, setMonthlyReports] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isMetropolitanHousing, setIsMetropolitanHousing] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showManageContactsModal, setShowManageContactsModal] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);

  // Properties pagination state
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [propertiesPerPage, setPropertiesPerPage] = useState(10);
  const [propertiesSearch, setPropertiesSearch] = useState('');
  const [propertiesStatusFilter, setPropertiesStatusFilter] = useState('All');

  // Certificates pagination state
  const [certificatesPage, setCertificatesPage] = useState(1);
  const [certificatesPerPage, setCertificatesPerPage] = useState(10);
  const [certificatesSearch, setCertificatesSearch] = useState('');
  const [certificateTypeFilter, setCertificateTypeFilter] = useState('All');
  const [certificateStatusFilter, setCertificateStatusFilter] = useState('All');
  const [certificatePropertyTypeFilter, setCertificatePropertyTypeFilter] = useState('Active only');

  // Blocks server-side pagination state
  const [blocksPage, setBlocksPage] = useState(1);
  const [blocksPerPage, setBlocksPerPage] = useState(10);
  const [blocksSearch, setBlocksSearch] = useState('');
  const [blocksTotalPages, setBlocksTotalPages] = useState(0);
  const [blocksTotalItems, setBlocksTotalItems] = useState(0);
  const [blocksLoading, setBlocksLoading] = useState(false);

  // Expenditure summary state (all properties of this client, date filtered)
  const [expDateFrom, setExpDateFrom] = useState(defaultExpenditureRange().date_from);
  const [expDateTo, setExpDateTo] = useState(defaultExpenditureRange().date_to);
  const [expAppliedRange, setExpAppliedRange] = useState(defaultExpenditureRange());
  const [expIncludeVoid, setExpIncludeVoid] = useState(false);
  const [expSummary, setExpSummary] = useState(null);
  const [expLoading, setExpLoading] = useState(false);
  const [expError, setExpError] = useState(null);
  const [expBreakdown, setExpBreakdown] = useState('category'); // 'category' | 'type'
  const [expQuickRange, setExpQuickRange] = useState('ytd');
  const [expPage, setExpPage] = useState(1);
  const [expPerPage, setExpPerPage] = useState(10);
  const [expSearch, setExpSearch] = useState('');

  const fetchClientData = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/api/clients/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => {
        if (!res.ok) {
          if (res.status === 403) {
            throw new Error('You are not authorized to view this client.');
          }
          if (res.status === 404) {
            throw new Error('Client not found.');
          }
          throw new Error('Failed to load client data');
        }
        return res.json();
      })
      .then(data => {
        setClient(data.client);
        setClientContacts(data.clientContacts || []);
        setProperties(data.properties || []);
        setClientNotes(data.clientNotes || []);
        // Don't set blocks here - they're fetched separately with pagination
        setMonthlyReports(data.monthlyReports || []);
        setDocuments(data.documents || []);
        setIsMetropolitanHousing(data.isMetropolitanHousing || false);
        setUserPermissions(data.userPermissions || {});
        setCurrentUser(data.currentUser);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  // Fetch blocks with server-side pagination
  const fetchBlocks = async (page = blocksPage, search = blocksSearch) => {
    setBlocksLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: blocksPerPage.toString(),
        search: search,
      });
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/blocks/client/${id}?${params}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setBlocks(data.data || []);
        setBlocksTotalPages(data.pagination?.totalPages || 0);
        setBlocksTotalItems(data.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error('Error fetching blocks:', err);
    } finally {
      setBlocksLoading(false);
    }
  };

  // Fetch the aggregated expenditure summary for every property of this client
  const fetchExpenditureSummary = async (range, includeVoid) => {
    setExpLoading(true);
    setExpError(null);
    try {
      const data = await expendituresService.getClientExpenditureSummary(id, {
        date_from: range.date_from || '',
        date_to: range.date_to || '',
        include_void: includeVoid,
      });
      if (data && data.success) {
        setExpSummary(data);
      } else {
        setExpSummary(null);
        setExpError((data && data.message) || 'Unable to load expenditure summary');
      }
    } catch (err) {
      setExpSummary(null);
      setExpError(
        err?.response?.data?.message || err.message || 'Unable to load expenditure summary'
      );
    } finally {
      setExpLoading(false);
    }
  };

  useEffect(() => {
    fetchClientData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch blocks when pagination or search changes
  useEffect(() => {
    if (id) {
      fetchBlocks(blocksPage, blocksSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, blocksPage, blocksPerPage, blocksSearch]);

  // Fetch expenditure summary when the applied date range changes (staff only)
  useEffect(() => {
    if (id && !isClientUser) {
      fetchExpenditureSummary(expAppliedRange, expIncludeVoid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isClientUser, expAppliedRange, expIncludeVoid]);

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!client) return <div>Client not found</div>;

  // Helper function to format address
  const formatAddress = () => {
    const parts = [];
    if (client.address_1) parts.push(client.address_1);
    if (client.address_2) parts.push(client.address_2);
    if (client.address_3) parts.push(client.address_3);
    return parts.join('<br>');
  };

  // Helper function to get contact email
  const getContactEmail = () => {
    const mainContact = clientContacts.find(contact => contact.main_contact);
    return mainContact?.user?.email || '';
  };

  // Property status options (matches calculatePropertyStatus output)
  const propertyStatusOptions = [
    'All',
    'Pre-Offer',
    'Under Offer',
    'Signed Proposal',
    'Maintenance',
    'Empty',
    'Not Full',
    'Full',
    'Active',
    'Rejected Offer',
    'Handing Back',
    'Ended',
    'Key Holding',
  ];

  // Filter and paginate properties
  const filteredProperties = properties.filter(property => {
    // Status filter (case-insensitive, since backend returns "Under offer" and "Under Offer")
    if (propertiesStatusFilter !== 'All') {
      const propStatus = (property.status || '').toString().toLowerCase();
      if (propStatus !== propertiesStatusFilter.toLowerCase()) return false;
    }

    if (!propertiesSearch) return true;
    const searchLower = propertiesSearch.toLowerCase();
    return (
      (property.name && property.name.toLowerCase().includes(searchLower)) ||
      (property.postcode && property.postcode.toLowerCase().includes(searchLower)) ||
      (property.id && property.id.toString().includes(searchLower))
    );
  });

  const totalPropertiesPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const paginatedProperties = filteredProperties.slice(
    (propertiesPage - 1) * propertiesPerPage,
    propertiesPage * propertiesPerPage
  );

  /* ---------------------------------------------------------------
   * Expenditure summary (staff only)
   * ------------------------------------------------------------- */
  const expTotals = expSummary?.totals || null;
  const expByProperty = expSummary?.byProperty || [];
  const expBreakdownRows =
    expBreakdown === 'category' ? expSummary?.byCategory || [] : expSummary?.byType || [];

  const filteredExpProperties = expByProperty.filter((row) => {
    if (!expSearch) return true;
    const searchLower = expSearch.toLowerCase();
    return (
      (row.property_name || '').toLowerCase().includes(searchLower) ||
      (row.postcode || '').toLowerCase().includes(searchLower) ||
      String(row.property_id).includes(searchLower)
    );
  });

  const totalExpPages = Math.ceil(filteredExpProperties.length / expPerPage);
  const paginatedExpProperties = filteredExpProperties.slice(
    (expPage - 1) * expPerPage,
    expPage * expPerPage
  );

  const applyExpenditureRange = () => {
    setExpPage(1);
    setExpQuickRange('custom');
    setExpAppliedRange({ date_from: expDateFrom, date_to: expDateTo });
  };

  const applyExpenditureQuickRange = (key) => {
    const today = new Date();
    let from = '';
    let to = toIsoDate(today);

    if (key === 'this-month') {
      from = toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1));
    } else if (key === 'last-3') {
      from = toIsoDate(new Date(today.getFullYear(), today.getMonth() - 3, today.getDate()));
    } else if (key === 'ytd') {
      from = `${today.getFullYear()}-01-01`;
    } else if (key === 'last-12') {
      from = toIsoDate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()));
    } else if (key === 'all') {
      from = '';
      to = '';
    }

    setExpDateFrom(from);
    setExpDateTo(to);
    setExpQuickRange(key);
    setExpPage(1);
    setExpAppliedRange({ date_from: from, date_to: to });
  };

  const expenditureRangeLabel = () => {
    const { date_from: from, date_to: to } = expAppliedRange;
    if (!from && !to) return 'All time';
    const pretty = (value) =>
      value ? new Date(`${value}T00:00:00`).toLocaleDateString('en-GB') : '';
    if (from && to) return `${pretty(from)} - ${pretty(to)}`;
    if (from) return `From ${pretty(from)}`;
    return `Up to ${pretty(to)}`;
  };

  const exportExpenditureSummaryCSV = () => {
    if (filteredExpProperties.length === 0) return;

    const rows = filteredExpProperties.map((row) => ({
      'Property': row.property_name,
      'Property ID': row.property_id,
      'Postcode': row.postcode,
      'Records': row.recordCount,
      'Total Amount': Number(row.amount).toFixed(2),
      'Paid': Number(row.amount_paid).toFixed(2),
      'Outstanding': Number(row.amount_outstanding).toFixed(2),
      '% of Total': row.percentage,
      'Last Expenditure': row.last_date || '',
    }));

    const headers = Object.keys(rows[0]);
    const csvRows = [headers.join(',')];
    rows.forEach((row) => {
      csvRows.push(
        headers.map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(',')
      );
    });

    const clientSlug = (client?.name || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    // BOM for proper UTF-8 handling in Excel
    const blob = new Blob(['﻿' + csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `expenditure-summary-${clientSlug}-${toIsoDate(new Date())}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Build export rows for company contacts
  const getContactsExportRows = () => {
    return clientContacts
      .filter((c) => c.user)
      .map((c) => ({
        'Name': `${c.user.first_name || ''} ${c.user.last_name || ''}`.trim(),
        'Job Title': c.user.job_title || '',
        'Department': c.department || '',
        'Phone': c.user.phone_number || '',
        'Mobile': c.user.mobile_number || '',
        'E-mail': c.user.email || '',
        'Main Contact': c.main_contact ? 'Yes' : 'No',
        'Internal Info': c.description || '',
      }));
  };

  const downloadClientContacts = async (format) => {
    if (!canExportContacts) return;
    const rows = getContactsExportRows();
    if (rows.length === 0) return;

    const clientSlug = (client?.name || 'client')
      .replace(/[^a-z0-9]+/gi, '-')
      .toLowerCase();
    const dateStr = new Date().toISOString().split('T')[0];

    if (format === 'csv') {
      const headers = Object.keys(rows[0]);
      const csvRows = [headers.join(',')];
      rows.forEach((row) => {
        csvRows.push(
          headers
            .map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`)
            .join(',')
        );
      });
      // BOM for proper UTF-8 handling in Excel
      const csvContent = '﻿' + csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts-${clientSlug}-${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return;
    }

    if (format === 'pdf') {
      try {
        const jsPDF = (await import('jspdf')).default;
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();

        // Header bar
        doc.setFillColor(45, 55, 72);
        doc.rect(0, 0, pageWidth, 60, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text('Company Contacts', 30, 28);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.text(client?.name || '', 30, 46);

        // Meta row
        doc.setTextColor(90, 90, 90);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, pageWidth - 30, 28, {
          align: 'right',
        });
        doc.text(`Total: ${rows.length}`, pageWidth - 30, 46, { align: 'right' });

        const headers = [
          '#',
          'Name',
          'Job Title',
          'Department',
          'Phone',
          'Mobile',
          'E-mail',
          'Main',
        ];
        const body = rows.map((r, idx) => [
          String(idx + 1),
          r['Name'],
          r['Job Title'],
          r['Department'],
          r['Phone'],
          r['Mobile'],
          r['E-mail'],
          r['Main Contact'],
        ]);

        autoTable(doc, {
          head: [headers],
          body,
          startY: 80,
          theme: 'striped',
          styles: { fontSize: 9, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.5 },
          headStyles: { fillColor: [245, 166, 35], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [250, 250, 250] },
          columnStyles: {
            0: { cellWidth: 30, halign: 'right' },
            7: { cellWidth: 50, halign: 'center' },
          },
        });

        doc.save(`contacts-${clientSlug}-${dateStr}.pdf`);
      } catch (err) {
        console.error('Failed to generate contacts PDF:', err);
        alert('Failed to generate PDF. Please try again.');
      }
    }
  };

  // Reset to page 1 when search changes
  const handlePropertiesSearch = (value) => {
    setPropertiesSearch(value);
    setPropertiesPage(1);
  };

  const handlePropertiesStatusFilter = (value) => {
    setPropertiesStatusFilter(value);
    setPropertiesPage(1);
  };

  // Build export rows from the currently filtered set
  const getPropertiesExportRows = () => {
    return filteredProperties.map((p) => ({
      'Property Name': p.name || '',
      'Property ID': p.id ?? '',
      'Postcode': p.postcode || 'N/A',
      'Status': p.status || 'N/A',
      'Taken On': p.date_taken_on ? new Date(p.date_taken_on).toLocaleDateString('en-GB') : 'N/A',
    }));
  };

  const handleExportPropertiesCSV = () => {
    const rows = getPropertiesExportRows();
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const csvRows = [headers.join(',')];
    rows.forEach((row) => {
      csvRows.push(
        headers
          .map((h) => `"${String(row[h] ?? '').replace(/"/g, '""')}"`)
          .join(',')
      );
    });
    const csvContent = 'data:text/csv;charset=utf-8,' + csvRows.join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    const clientSlug = (client?.name || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    link.setAttribute(
      'download',
      `properties-${clientSlug}-${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPropertiesPDF = async () => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const autoTable = (await import('jspdf-autotable')).default;

      const rows = getPropertiesExportRows();
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Header bar
      doc.setFillColor(45, 55, 72);
      doc.rect(0, 0, pageWidth, 60, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Properties Report', 30, 28);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.text(client?.name || '', 30, 46);

      // Meta row
      doc.setTextColor(90, 90, 90);
      doc.setFontSize(9);
      const generatedStr = `Generated: ${new Date().toLocaleString('en-GB')}`;
      doc.text(generatedStr, pageWidth - 30, 28, { align: 'right' });
      const filterStr = `Status: ${propertiesStatusFilter}  |  Total: ${rows.length}`;
      doc.text(filterStr, pageWidth - 30, 46, { align: 'right' });

      const headers = ['#', 'Property Name', 'Property ID', 'Postcode', 'Status', 'Taken On'];
      const body = rows.map((r, idx) => [
        String(idx + 1),
        r['Property Name'],
        String(r['Property ID']),
        r['Postcode'],
        r['Status'],
        r['Taken On'],
      ]);

      autoTable(doc, {
        head: [headers],
        body,
        startY: 80,
        theme: 'striped',
        styles: { fontSize: 9, cellPadding: 5, lineColor: [220, 220, 220], lineWidth: 0.5 },
        headStyles: { fillColor: [245, 166, 35], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        columnStyles: {
          0: { cellWidth: 30, halign: 'right' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 80 },
          3: { cellWidth: 80 },
          4: { cellWidth: 100 },
          5: { cellWidth: 80 },
        },
        margin: { left: 30, right: 30 },
        didDrawPage: (data) => {
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Page ${doc.internal.getNumberOfPages()}`,
            pageWidth - 30,
            pageHeight - 15,
            { align: 'right' }
          );
        },
      });

      const clientSlug = (client?.name || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      doc.save(`properties-${clientSlug}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('Error exporting properties PDF:', err);
    }
  };

  // Certificate type options
  const certificateTypes = [
    'All',
    'Gas Safe Certificate',
    'NIC Certificate',
    'Asbestos Register',
    'Fire Risk Assessment',
    'Legionella',
    'Fire Alarm',
    'Emergency Lighting',
    'Fire Extinguisher Certificate',
    'PAT Testing',
    '240V Certificate'
  ];

  // Certificate status options
  const certificateStatuses = ['All', 'Active', 'Archived', 'Due to expire', 'Expired'];

  // Filter and paginate certificates
  const certificates = client?.certificates || [];
  const filteredCertificates = certificates.filter(cert => {
    // Property Type filter - "Active only" uses Laravel's active property definition
    // (client is live, date_live <= now, date_taken_back is null or future, date_notice is null)
    if (certificatePropertyTypeFilter === 'Active only' && !cert.is_property_active) {
      return false;
    }

    // Type filter
    if (certificateTypeFilter !== 'All' && cert.type !== certificateTypeFilter) {
      return false;
    }

    // Status filter (matching Laravel's DocumentsController logic)
    if (certificateStatusFilter !== 'All') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const expiration = cert.expiration ? new Date(cert.expiration) : null;
      if (expiration) expiration.setHours(0, 0, 0, 0);
      const fourWeeksFromNow = new Date(now.getTime() + 28 * 24 * 60 * 60 * 1000); // 4 weeks = 28 days

      // Active: not expired (expiration >= now) AND not archived (status = 1)
      if (certificateStatusFilter === 'Active' && (cert.status === 'archived' || !expiration || expiration < now)) return false;
      // Expired: expiration < now AND not archived
      if (certificateStatusFilter === 'Expired' && (cert.status === 'archived' || !expiration || expiration >= now)) return false;
      // Due to expire: within next 4 weeks (expiration > now AND expiration <= 4 weeks) AND not archived
      if (certificateStatusFilter === 'Due to expire' && (cert.status === 'archived' || !expiration || expiration <= now || expiration > fourWeeksFromNow)) return false;
      // Archived: status = 0 (archived)
      if (certificateStatusFilter === 'Archived' && cert.status !== 'archived') return false;
    }

    // Search filter
    if (certificatesSearch) {
      const searchLower = certificatesSearch.toLowerCase();
      return (
        (cert.type && cert.type.toLowerCase().includes(searchLower)) ||
        (cert.property_name && cert.property_name.toLowerCase().includes(searchLower)) ||
        (cert.name && cert.name.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  const totalCertificatesPages = Math.ceil(filteredCertificates.length / certificatesPerPage);
  const paginatedCertificates = filteredCertificates.slice(
    (certificatesPage - 1) * certificatesPerPage,
    certificatesPage * certificatesPerPage
  );

  // Reset to page 1 when certificates filters change
  const handleCertificatesSearch = (value) => {
    setCertificatesSearch(value);
    setCertificatesPage(1);
  };

  const handleCertificateTypeFilter = (type) => {
    setCertificateTypeFilter(type);
    setCertificatesPage(1);
  };

  const handleCertificateStatusFilter = (status) => {
    setCertificateStatusFilter(status);
    setCertificatesPage(1);
  };

  // Handle blocks search
  const handleBlocksSearch = (value) => {
    setBlocksSearch(value);
    setBlocksPage(1);
  };

  // Handle block deletion
  const handleDeleteBlock = async (blockId, blockName) => {
    if (!window.confirm(`Are you sure you want to remove block "${blockName}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/blocks/${blockId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        // Refresh blocks list
        fetchBlocks(blocksPage, blocksSearch);
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to delete block');
      }
    } catch (error) {
      console.error('Error deleting block:', error);
      alert('Failed to delete block');
    }
  };

  return (
    <div className={`container-fluid ${styles.pageWrapper}`}>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbRow}>
        <span className={styles.breadcrumbMain} onClick={() => navigate('/dashboard')}>Dashboard</span>
        <div style={{ background: '#30C693', display: 'flex', alignItems: 'center' }}>
          <span className={styles.breadcrumbSep}>/</span>
          <Link to="/clients" className={styles.breadcrumbLink} style={{ color: '#fff', padding: '2px 8px' }}>Clients</Link>
        </div>
        <div style={{ background: '#30C693', display: 'flex', alignItems: 'center' }}>
          <span className={styles.breadcrumbSep} style={{ color: '#fff' }}>/</span>
          <span className={styles.breadcrumbCurrent}>{client.name}</span>
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.actionButtonsRow}>
        {userPermissions.canEdit && (
          <Link to={`/clients/${id}/edit`} className={styles.actionBtn}>
            <i className="glyphicon glyphicon-edit"></i> Edit Client
          </Link>
        )}

        {userPermissions.canCreateProperty && (
          <Link to={`/properties/create?client_id=${id}`} className={styles.actionBtn}>
            <i className="glyphicon glyphicon-plus"></i> Add Property
          </Link>
        )}

        {userPermissions.canViewTickets && (
          <>
            <Link to={`/clients/${id}/tickets`} className={styles.actionBtn}>
              Tickets
            </Link>
            <Link to={`/clients/${id}/tickets/cost-of-works`} className={styles.actionBtn}>
              Cost of works
            </Link>
          </>
        )}

        {isMetropolitanHousing && (
          <Link to={`/vip/client/${id}`} className={styles.actionBtn}>
            <i className="fa fa-money"></i> VIP
          </Link>
        )}
      </div>

      {/* Main Content */}
      <div className={`${styles.row} ${styles.marginDown} ${styles.mt3}`}>
        {/* Company Overview */}
        <div className={styles.col6}>
          <div className={styles.panelTitle}>
            <h3>Company overview</h3>
          </div>
          <div className={`${styles.panelGroup} ${styles.marginDown}`}>
            {client.logo && (
              <div className="company-logo">
                <img className="account-avatar" src={client.logo} alt="Company Logo" />
              </div>
            )}
            <dl className={styles.greenList}>
              <dt>Status:</dt>
              <dd>{client.status?.name || 'N/A'}</dd>
              <dt>Company name:</dt>
              <dd>{client.name}</dd>
              <dt>Company address:</dt>
              <dd dangerouslySetInnerHTML={{ __html: formatAddress() || 'N/A' }}></dd>
              <dt>Postcode:</dt>
              <dd>{client.postcode || 'N/A'}</dd>
              <dt>Phone:</dt>
              <dd>{client.phone_number || 'N/A'}</dd>
              <dt>Email:</dt>
              <dd className={styles.emailDd}>{getContactEmail() || 'N/A'}</dd>
              <dt>Fax:</dt>
              <dd>{client.fax_number || 'N/A'}</dd>
              <dt>Web:</dt>
              <dd>{client.web || 'N/A'}</dd>

              {client.leadSource && (
                <>
                  <dt>Lead Source:</dt>
                  <dd>{client.leadSource.name}</dd>
                </>
              )}

              {client.marketSegment && (
                <>
                  <dt>Market Segment:</dt>
                  <dd>{client.marketSegment.name}</dd>
                </>
              )}

              {client.bankAccount && (
                <>
                  <dt>GG Bank Account:</dt>
                  <dd>{client.bankAccount.name}</dd>
                  <dt>Bank Details:</dt>
                  <dd>{client.bank_details_account_name}</dd>
                  <dt></dt>
                  <dd>{client.bank_details_bank_name}</dd>
                  <dt></dt>
                  <dd>{client.bank_details_sort_code}</dd>
                  <dt></dt>
                  <dd>{client.bank_details_number}</dd>
                </>
              )}

              <dt>Internal Info:</dt>
              <dd>
                {userPermissions.canEdit ? (
                  <textarea
                    className="form-control js-client-update-field-text width-100"
                    rows="8"
                    defaultValue={client.internal_info || ''}
                    style={{ width: '100%' }}
                    onBlur={async (e) => {
                      const newValue = e.target.value;
                      if (newValue !== client.internal_info) {
                        try {
                          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/clients/update-field`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${localStorage.getItem('token')}`,
                            },
                            body: JSON.stringify({
                              id: client.id,
                              field: 'internal_info',
                              value: newValue,
                            }),
                          });

                          if (response.ok) {
                            fetchClientData();
                          } else {
                            alert('Failed to update internal info');
                          }
                        } catch (error) {
                          console.error('Error updating internal_info:', error);
                          alert('Failed to update internal info');
                        }
                      }
                    }}
                  />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: (client.internal_info || '').replace(/\n/g, '<br>') }}></div>
                )}
                
                <span id="edited-internalinfo" className="pull-left" style={{ fontSize: 'smaller' }}>
                  <i>Last edited by
                    {client.internal_info_edited_by && client.internal_info_edited_at && (
                      <>
                        {client.internalInfoEditor && ` ${client.internalInfoEditor.first_name} ${client.internalInfoEditor.last_name}`} 
                        at {new Date(client.internal_info_edited_at).toLocaleString()}
                      </>
                    )}
                  </i>
                </span>
              </dd>

              <dt>Region:</dt>
              <dd>{client.region || 'N/A'}</dd>
            </dl>
          </div>
        </div>

        {/* Account Manager */}
        <div className={styles.col6}>
          <div className={styles.panelTitle}>
            <h3>Account Manager</h3>
          </div>
          <div className={`${styles.panelGroup} ${styles.marginDown}`}>
            {client.manager && (
              <dl className={styles.greenList}>
                <dt>Name:</dt>
                <dd>{`${client.manager.first_name} ${client.manager.last_name}`}</dd>
                <dt>Phone number:</dt>
                <dd>{client.manager.phone_number || 'N/A'}</dd>
                <dt>E-mail:</dt>
                <dd>{client.manager.email}</dd>
              </dl>
            )}
          </div>

          {/* Savings and VIP Section - Positioned directly below Account Manager */}
          <div className={styles.panelTitle}>
            <h3>Property savings & voids into profit to date</h3>
          </div>
          <div className={styles.panelGroup}>
            <div className={styles.savingBoxRed}>
              <div className={styles.savingBoxTop}>
                <img src="/images/peppa-red.png" alt="Savings" />
                <p className={styles.cash}>+£{parseFloat(client.savings_made || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <p className={styles.savingInformation}>SAVINGS MADE BY USING GLOBAL GUARDIANS</p>
            </div>
            <div className={styles.savingBoxMint}>
              <div className={styles.savingBoxTop}>
                <img src="/images/hand-mint.png" alt="VIP" />
                <p className={styles.cash}>+£{parseFloat(client.vip_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              </div>
              <p className={styles.savingInformation}>INCOME GENERATED THROUGH VOIDS INTO PROFIT</p>
            </div>
          </div>
        </div>
      </div>

      {/* Company Contacts */}
      <div className={styles.row}>
        <div className={styles.col12}>
          <div className={styles.titleContact}>
            <h3 className={styles.mrAuto}>Company contacts</h3>

            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => downloadClientContacts('csv')}
              style={{ display: canExportContacts ? undefined : 'none' }}
              disabled={clientContacts.length === 0}
              title="Download contacts as CSV"
            >
              <i className="glyphicon glyphicon-download-alt"></i> CSV
            </button>

            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => downloadClientContacts('pdf')}
              style={{ display: canExportContacts ? undefined : 'none' }}
              disabled={clientContacts.length === 0}
              title="Download contacts as PDF"
            >
              <i className="glyphicon glyphicon-download-alt"></i> PDF
            </button>

            {userPermissions.canEdit && (
              <button className={styles.actionBtn} onClick={() => setShowAddContactModal(true)}>
                <i className="glyphicon glyphicon-plus"></i> Add company contact
              </button>
            )}

            <button className={styles.actionBtn} onClick={() => setShowManageContactsModal(true)}>
              <i className="glyphicon glyphicon-edit"></i> Manage Contacts
            </button>
          </div>
          <div className={`${styles.panelGroup} ${styles.marginDown}`}>
            {(showAllContacts ? clientContacts : clientContacts.slice(0, 10)).map((contact) => (
              <div key={contact.id} className={styles.contactRow}>
                {contact.user && (
                  <>
                    {/* Section 1: Name, Job title, Department */}
                    <div className={styles.contactSection}>
                      {contact.main_contact && (
                        <span className={styles.mainContactBadge}>Main Contact</span>
                      )}
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Name:</span>
                        <span className={styles.contactValue}>{`${contact.user.first_name} ${contact.user.last_name}`}</span>
                      </div>
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Job title:</span>
                        <span className={styles.contactValue}>{contact.user.job_title || 'N/A'}</span>
                      </div>
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Department:</span>
                        <span className={styles.contactValue}>{contact.department || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Section 2: Phone, Mobile, E-mail */}
                    <div className={styles.contactSection}>
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Phone:</span>
                        <span className={styles.contactValue}>{contact.user.phone_number || 'N/A'}</span>
                      </div>
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Mobile:</span>
                        <span className={styles.contactValue}>{contact.user.mobile_number || 'N/A'}</span>
                      </div>
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>E-mail:</span>
                        <span className={styles.contactValue}>
                          <a href={`mailto:${contact.user.email}`}>{contact.user.email}</a>
                        </span>
                      </div>
                    </div>

                    {/* Section 3: Main Contact checkbox, Internal info */}
                    <div className={styles.contactSection}>
                      <div className={styles.contactItem}>
                        <span className={styles.contactLabel}>Main Contact:</span>
                        <input
                          type="checkbox"
                          checked={contact.main_contact}
                          onChange={async (e) => {
                            const isChecked = e.target.checked;
                            try {
                              const response = await fetch(`${process.env.REACT_APP_API_URL}/api/clients/contacts/main-contact`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                                },
                                body: JSON.stringify({
                                  contactId: contact.id,
                                  value: isChecked ? 1 : 0,
                                }),
                              });

                              if (response.ok) {
                                fetchClientData();
                              } else {
                                alert('Failed to update main contact');
                                e.target.checked = !isChecked;
                              }
                            } catch (error) {
                              console.error('Error updating main contact:', error);
                              alert('Failed to update main contact');
                              e.target.checked = !isChecked;
                            }
                          }}
                        />
                      </div>
                      {contact.description && (
                        <div className={styles.internalInfo}>
                          <span className={styles.contactLabel}>Internal info:</span> {contact.description}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Show More / Show Less Button */}
            {clientContacts.length > 10 && (
              <div className={styles.showMoreContainer}>
                <button
                  className={styles.showMoreBtn}
                  onClick={() => setShowAllContacts(!showAllContacts)}
                >
                  {showAllContacts ? 'Show Less' : `Show More (${clientContacts.length - 10} more)`}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className={styles.tableSection}>
        <div className={styles.tablePanelHeader}>
          <div className={styles.tablePanelTitle}>Properties</div>
        </div>
          {properties.length > 0 ? (
            <>
              <div className={styles.tableControls}>
                <div className={styles.tableControlsLeft}>
                  <span>Show</span>
                  <select
                    className={styles.tableSelect}
                    value={propertiesPerPage}
                    onChange={(e) => {
                      setPropertiesPerPage(Number(e.target.value));
                      setPropertiesPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
                <div className={styles.tableControlsRight}>
                  <span>Status</span>
                  <select
                    className={styles.tableSelect}
                    value={propertiesStatusFilter}
                    onChange={(e) => handlePropertiesStatusFilter(e.target.value)}
                  >
                    {propertyStatusOptions.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginLeft: '0.5rem' }}
                    onClick={handleExportPropertiesCSV}
                    disabled={filteredProperties.length === 0}
                  >
                    Excel
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginLeft: '0.25rem' }}
                    onClick={handleExportPropertiesPDF}
                    disabled={filteredProperties.length === 0}
                  >
                    PDF
                  </button>
                  <input
                    type="text"
                    className={styles.tableSearch}
                    value={propertiesSearch}
                    onChange={(e) => handlePropertiesSearch(e.target.value)}
                    placeholder="Search properties..."
                  />
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Property Name</th>
                      <th>Property ID</th>
                      <th>Postcode</th>
                      <th>Status</th>
                      <th>Taken On</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProperties.map((property) => (
                      <tr key={property.id}>
                        <td>
                          <Link to={`/properties/${property.id}`}>
                            {property.name}
                          </Link>
                        </td>
                        <td>{property.id}</td>
                        <td>{property.postcode || 'N/A'}</td>
                        <td>{property.status || 'N/A'}</td>
                        <td>{property.date_taken_on ? new Date(property.date_taken_on).toLocaleDateString('en-GB') : 'N/A'}</td>
                        <td>
                          <Link to={`/properties/${property.id}`} className={styles.btnShow}>
                            Show
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.tablePagination}>
                <div className={styles.paginationInfo}>
                  Showing {filteredProperties.length === 0 ? 0 : ((propertiesPage - 1) * propertiesPerPage) + 1} to {Math.min(propertiesPage * propertiesPerPage, filteredProperties.length)} of {filteredProperties.length} entries
                  {propertiesSearch && ` (filtered from ${properties.length} total entries)`}
                </div>
                <ul className={styles.pagination}>
                  <li className={propertiesPage === 1 ? styles.disabled : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); if (propertiesPage > 1) setPropertiesPage(p => p - 1); }}>
                      Previous
                    </a>
                  </li>
                  {(() => {
                    const pages = [];
                    const maxPages = 5;
                    let startPage = Math.max(1, propertiesPage - 2);
                    let endPage = Math.min(totalPropertiesPages, startPage + maxPages - 1);
                    if (endPage - startPage < maxPages - 1) {
                      startPage = Math.max(1, endPage - maxPages + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <li key={1} className={propertiesPage === 1 ? styles.active : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setPropertiesPage(1); }}>1</a>
                        </li>
                      );
                      if (startPage > 2) {
                        pages.push(<li key="ellipsis-start" className={styles.disabled}><span>...</span></li>);
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <li key={i} className={propertiesPage === i ? styles.active : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setPropertiesPage(i); }}>{i}</a>
                        </li>
                      );
                    }

                    if (endPage < totalPropertiesPages) {
                      if (endPage < totalPropertiesPages - 1) {
                        pages.push(<li key="ellipsis-end" className={styles.disabled}><span>...</span></li>);
                      }
                      pages.push(
                        <li key={totalPropertiesPages} className={propertiesPage === totalPropertiesPages ? styles.active : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setPropertiesPage(totalPropertiesPages); }}>{totalPropertiesPages}</a>
                        </li>
                      );
                    }
                    return pages;
                  })()}
                  <li className={propertiesPage === totalPropertiesPages || totalPropertiesPages === 0 ? styles.disabled : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); if (propertiesPage < totalPropertiesPages) setPropertiesPage(p => p + 1); }}>
                      Next
                    </a>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <p className={styles.noData}>No properties found for this client.</p>
          )}
      </div>

      {/* Expenditure Summary Section - internal only, never rendered for CLIENT users */}
      {!isClientUser && (
        <div className={styles.tableSection}>
          <div className={styles.tablePanelHeader}>
            <div className={styles.tablePanelTitle}>Expenditure summary</div>
          </div>

          <div className={styles.expFilters}>
            <div className={styles.expQuickRanges}>
              {[
                { key: 'this-month', label: 'This month' },
                { key: 'last-3', label: 'Last 3 months' },
                { key: 'ytd', label: 'Year to date' },
                { key: 'last-12', label: 'Last 12 months' },
                { key: 'all', label: 'All time' },
              ].map((range) => (
                <button
                  key={range.key}
                  type="button"
                  className={`${styles.expQuickBtn} ${expQuickRange === range.key ? styles.expQuickBtnActive : ''}`}
                  onClick={() => applyExpenditureQuickRange(range.key)}
                >
                  {range.label}
                </button>
              ))}
            </div>

            <div className={styles.expFilterRow}>
              <label className={styles.expFilterField}>
                <span>From</span>
                <input
                  type="date"
                  className={styles.expDateInput}
                  value={expDateFrom}
                  max={expDateTo || undefined}
                  onChange={(e) => setExpDateFrom(e.target.value)}
                />
              </label>
              <label className={styles.expFilterField}>
                <span>To</span>
                <input
                  type="date"
                  className={styles.expDateInput}
                  value={expDateTo}
                  min={expDateFrom || undefined}
                  onChange={(e) => setExpDateTo(e.target.value)}
                />
              </label>
              <button type="button" className="btn btn-secondary" onClick={applyExpenditureRange}>
                Apply
              </button>
              <label className={styles.expCheckbox}>
                <input
                  type="checkbox"
                  checked={expIncludeVoid}
                  onChange={(e) => {
                    setExpPage(1);
                    setExpIncludeVoid(e.target.checked);
                  }}
                />
                Include void entries
              </label>
              <span className={styles.expRangeLabel}>Showing: {expenditureRangeLabel()}</span>
            </div>
          </div>

          {expLoading ? (
            <p className={styles.noData}>Loading expenditure summary...</p>
          ) : expError ? (
            <p className={styles.noData}>{expError}</p>
          ) : !expTotals || expTotals.recordCount === 0 ? (
            <p className={styles.noData}>
              No expenditure recorded for this client in the selected date range.
            </p>
          ) : (
            <>
              <div className={styles.expKpiRow}>
                <div className={styles.expKpiCard}>
                  <div className={styles.expKpiLabel}>Total expenditure</div>
                  <div className={styles.expKpiValue}>{formatMoney(expTotals.totalAmount)}</div>
                  <div className={styles.expKpiSub}>{expTotals.recordCount} entries</div>
                </div>
                <div className={styles.expKpiCard}>
                  <div className={styles.expKpiLabel}>Paid</div>
                  <div className={`${styles.expKpiValue} ${styles.expKpiPaid}`}>
                    {formatMoney(expTotals.totalPaid)}
                  </div>
                  <div className={styles.expKpiSub}>{expTotals.paidPercentage}% of total</div>
                </div>
                <div className={styles.expKpiCard}>
                  <div className={styles.expKpiLabel}>Outstanding</div>
                  <div className={`${styles.expKpiValue} ${styles.expKpiOutstanding}`}>
                    {formatMoney(expTotals.totalOutstanding)}
                  </div>
                  <div className={styles.expKpiSub}>
                    {expTotals.recordCount - expTotals.paidCount} unpaid entries
                  </div>
                </div>
                <div className={styles.expKpiCard}>
                  <div className={styles.expKpiLabel}>Properties with spend</div>
                  <div className={styles.expKpiValue}>
                    {expTotals.propertiesWithExpenditure}
                    <span className={styles.expKpiOf}> / {expTotals.totalClientProperties}</span>
                  </div>
                  <div className={styles.expKpiSub}>of this client's properties</div>
                </div>
                <div className={styles.expKpiCard}>
                  <div className={styles.expKpiLabel}>Average per property</div>
                  <div className={styles.expKpiValue}>
                    {formatMoney(
                      expTotals.propertiesWithExpenditure > 0
                        ? expTotals.totalAmount / expTotals.propertiesWithExpenditure
                        : 0
                    )}
                  </div>
                  <div className={styles.expKpiSub}>across properties with spend</div>
                </div>
              </div>

              <div className={styles.tableControls}>
                <div className={styles.tableControlsLeft}>
                  <span>Show</span>
                  <select
                    className={styles.tableSelect}
                    value={expPerPage}
                    onChange={(e) => {
                      setExpPerPage(Number(e.target.value));
                      setExpPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
                <div className={styles.tableControlsRight}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginRight: '0.5rem' }}
                    onClick={exportExpenditureSummaryCSV}
                    disabled={filteredExpProperties.length === 0}
                  >
                    Excel
                  </button>
                  <input
                    type="text"
                    className={styles.tableSearch}
                    value={expSearch}
                    onChange={(e) => {
                      setExpSearch(e.target.value);
                      setExpPage(1);
                    }}
                    placeholder="Search properties..."
                  />
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Postcode</th>
                      <th>Entries</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Outstanding</th>
                      <th>% of total</th>
                      <th>Last entry</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedExpProperties.map((row) => (
                      <tr key={row.property_id}>
                        <td>
                          <Link to={`/properties/${row.property_id}`}>{row.property_name}</Link>
                        </td>
                        <td>{row.postcode}</td>
                        <td>{row.recordCount}</td>
                        <td className={styles.expAmountCell}>{formatMoney(row.amount)}</td>
                        <td className={styles.expAmountCell}>{formatMoney(row.amount_paid)}</td>
                        <td
                          className={`${styles.expAmountCell} ${row.amount_outstanding > 0 ? styles.expKpiOutstanding : ''}`}
                        >
                          {formatMoney(row.amount_outstanding)}
                        </td>
                        <td>
                          <div className={styles.expBarCell}>
                            <div className={styles.expBar}>
                              <div
                                className={styles.expBarFill}
                                style={{ width: `${Math.min(row.percentage, 100)}%` }}
                              />
                            </div>
                            <span>{row.percentage}%</span>
                          </div>
                        </td>
                        <td>{row.last_date || '-'}</td>
                        <td>
                          <Link
                            to={`/properties/${row.property_id}/expenditures`}
                            className={styles.btnShow}
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className={styles.expTotalsRow}>
                      <td colSpan={2}>Total ({filteredExpProperties.length} properties)</td>
                      <td>
                        {filteredExpProperties.reduce((sum, row) => sum + row.recordCount, 0)}
                      </td>
                      <td className={styles.expAmountCell}>
                        {formatMoney(filteredExpProperties.reduce((sum, row) => sum + row.amount, 0))}
                      </td>
                      <td className={styles.expAmountCell}>
                        {formatMoney(
                          filteredExpProperties.reduce((sum, row) => sum + row.amount_paid, 0)
                        )}
                      </td>
                      <td className={styles.expAmountCell}>
                        {formatMoney(
                          filteredExpProperties.reduce(
                            (sum, row) => sum + row.amount_outstanding,
                            0
                          )
                        )}
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className={styles.tablePagination}>
                <div className={styles.paginationInfo}>
                  Showing {filteredExpProperties.length === 0 ? 0 : (expPage - 1) * expPerPage + 1} to{' '}
                  {Math.min(expPage * expPerPage, filteredExpProperties.length)} of{' '}
                  {filteredExpProperties.length} properties
                  {expSearch && ` (filtered from ${expByProperty.length} total)`}
                </div>
                <ul className={styles.pagination}>
                  <li className={expPage === 1 ? styles.disabled : ''}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (expPage > 1) setExpPage((p) => p - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {Array.from({ length: totalExpPages }, (_, index) => index + 1).map((page) => (
                    <li key={page} className={expPage === page ? styles.active : ''}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setExpPage(page);
                        }}
                      >
                        {page}
                      </a>
                    </li>
                  ))}
                  <li className={expPage === totalExpPages || totalExpPages === 0 ? styles.disabled : ''}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (expPage < totalExpPages) setExpPage((p) => p + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </div>

              {/* Breakdown by category / type */}
              <div className={styles.expBreakdownHeader}>
                <h4 className={styles.expBreakdownTitle}>Breakdown</h4>
                <div className={styles.expTabs}>
                  <button
                    type="button"
                    className={`${styles.expTab} ${expBreakdown === 'category' ? styles.expTabActive : ''}`}
                    onClick={() => setExpBreakdown('category')}
                  >
                    By category
                  </button>
                  <button
                    type="button"
                    className={`${styles.expTab} ${expBreakdown === 'type' ? styles.expTabActive : ''}`}
                    onClick={() => setExpBreakdown('type')}
                  >
                    By type
                  </button>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Category</th>
                      {expBreakdown === 'type' && <th>Type</th>}
                      <th>Entries</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Outstanding</th>
                      <th>% of total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expBreakdownRows.map((row, index) => (
                      <tr key={`${expBreakdown}-${row.type_id ?? row.category_id ?? 'none'}-${index}`}>
                        <td>{row.category_name}</td>
                        {expBreakdown === 'type' && <td>{row.type_name}</td>}
                        <td>{row.recordCount}</td>
                        <td className={styles.expAmountCell}>{formatMoney(row.amount)}</td>
                        <td className={styles.expAmountCell}>{formatMoney(row.amount_paid)}</td>
                        <td
                          className={`${styles.expAmountCell} ${row.amount_outstanding > 0 ? styles.expKpiOutstanding : ''}`}
                        >
                          {formatMoney(row.amount_outstanding)}
                        </td>
                        <td>
                          <div className={styles.expBarCell}>
                            <div className={styles.expBar}>
                              <div
                                className={styles.expBarFill}
                                style={{ width: `${Math.min(row.percentage, 100)}%` }}
                              />
                            </div>
                            <span>{row.percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Associated Documents Section */}
      <div className="row">
        <div className="col-md-12">
          <div className="panel-title title-standard js-property-documents">
            <h3 className="mr-auto">Client documents</h3>
            {userPermissions.canCreateDocument && (
              <Link to={`/clients/${id}/documents/create`} className="btn btn-secondary">
                <i className="glyphicon glyphicon-plus"></i> Add document
              </Link>
            )}
          </div>

          <div className="panel-group border-down margin-down">
            {[
              'Completed Client Objection Handling Form',
              'Completed Proposal',
              'Negotiation Form',
              'Completed Client Onboarding Form',
              'Meeting Notes',
              'Empathy Map',
              'Tender Document',
              'Company Strategy/Marketing Material',
              'Finance Report',
              'Miscellaneous'
            ].map((category) => {
              const categoryDocs = documents.filter(doc => doc.collection_name === category);
              return (
                <div key={category}>
                  <h4 className="report-section-header mt-30">{category}</h4>
                  {categoryDocs.length > 0 ? (
                    <div className="row">
                      {categoryDocs.map((doc) => (
                        <div key={doc.id} className="col-xs-12 col-sm-4">
                          <a href={doc.url} target="_blank" rel="noopener noreferrer" className="hidden-print-link">
                            <div className="btn-property sameHeight">
                              <div className="document-left">
                                <img src="/images/pdf.png" alt="Document" />
                              </div>
                              <div className="document-right">
                                <p className="btn-title">{doc.name}</p>
                                <p className="btn-short">Click to open</p>
                              </div>
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>There are no documents.</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Blocks Section */}
      <div className={styles.tableSection}>
        <div className={styles.tablePanelHeader}>
          <div className={styles.tablePanelTitle}>Blocks</div>
          {userPermissions.canManageBlocks && (
            <Link to={`/blocks/create?client_id=${id}`} className="btn btn-secondary">
              <i className="glyphicon glyphicon-plus"></i> Add block
            </Link>
          )}
        </div>

        {/* Search and Per Page Controls */}
        <div className={styles.tableControls} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label>Show</label>
            <select
              value={blocksPerPage}
              onChange={(e) => {
                setBlocksPerPage(Number(e.target.value));
                setBlocksPage(1);
              }}
              className="form-control"
              style={{ width: '70px', display: 'inline-block' }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <label>entries</label>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label>Search:</label>
            <input
              type="text"
              value={blocksSearch}
              onChange={(e) => handleBlocksSearch(e.target.value)}
              className="form-control"
              style={{ width: '200px' }}
              placeholder="Search blocks..."
            />
          </div>
        </div>

        {blocksLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading blocks...</div>
        ) : blocks.length > 0 ? (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>No. properties</th>
                    {userPermissions.canManageBlocks && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((block) => (
                    <tr key={block.id}>
                      <td>
                        <Link to={`/blocks/${block.id}`} style={{ fontWeight: 700 }}>
                          {block.name}
                        </Link>
                      </td>
                      <td>{block.properties_count || 0}</td>
                      {userPermissions.canManageBlocks && (
                        <td>
                          <Link to={`/blocks/${block.id}`} className="btn btn-sm btn-warning" style={{ marginRight: '5px' }}>
                            Show
                          </Link>
                          <Link to={`/blocks/${block.id}/edit`} className="btn btn-sm btn-primary" style={{ marginRight: '5px' }}>
                            Edit
                          </Link>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteBlock(block.id, block.name)}
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderTop: '1px solid #ddd' }}>
              <div>
                Showing {((blocksPage - 1) * blocksPerPage) + 1} to {Math.min(blocksPage * blocksPerPage, blocksTotalItems)} of {blocksTotalItems} entries
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  className="btn btn-sm btn-default"
                  onClick={() => setBlocksPage(1)}
                  disabled={blocksPage === 1}
                >
                  First
                </button>
                <button
                  className="btn btn-sm btn-default"
                  onClick={() => setBlocksPage(blocksPage - 1)}
                  disabled={blocksPage === 1}
                >
                  Previous
                </button>
                <span style={{ padding: '5px 10px', background: '#f5f5f5', borderRadius: '3px' }}>
                  Page {blocksPage} of {blocksTotalPages || 1}
                </span>
                <button
                  className="btn btn-sm btn-default"
                  onClick={() => setBlocksPage(blocksPage + 1)}
                  disabled={blocksPage >= blocksTotalPages}
                >
                  Next
                </button>
                <button
                  className="btn btn-sm btn-default"
                  onClick={() => setBlocksPage(blocksTotalPages)}
                  disabled={blocksPage >= blocksTotalPages}
                >
                  Last
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className={styles.noData}>No block!</p>
        )}
      </div>

      {/* Client Reports Section */}
      {monthlyReports.length > 0 && (
        <div className={styles.row}>
          <div className={styles.col12}>
            <div className={styles.panelTitle}>
              <h3>Reports</h3>
            </div>
            <div className={`${styles.panelGroup} ${styles.marginDown}`}>
              <div className={styles.reportsRow}>
                {monthlyReports.map((report) => (
                  <div key={report.id} className={styles.reportItem}>
                    <img src="/images/pdf.png" alt="Report" className={styles.reportIcon} />
                    <div
                      className={styles.reportInfo}
                      onClick={async () => {
                        try {
                          const response = await fetch(
                            `${process.env.REACT_APP_API_URL}/api/monthlyinspectionreports/${report.id}/download-pdf`,
                            {
                              headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                              },
                            }
                          );
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `report_${report.month_name || 'monthly'}_${report.year}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } else {
                            alert('Unable to download report');
                          }
                        } catch (error) {
                          console.error('Download error:', error);
                          alert('Error downloading report');
                        }
                      }}
                    >
                      <p className={styles.reportTitle}>{report.month_name} ({report.year})</p>
                      <p className={styles.reportDate}>Updated: {new Date(report.updated_at).toLocaleDateString('en-GB')}</p>
                    </div>
                    {userPermissions.canManageReports && (
                      <div className={styles.reportActions}>
                        <button
                          className={styles.btnRemove}
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to remove?')) {
                              try {
                                await fetch(`${process.env.REACT_APP_API_URL}/api/monthly-inspections/${id}/summary/${report.id}`, {
                                  method: 'DELETE',
                                  headers: {
                                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                                  },
                                });
                                fetchClientData();
                              } catch (error) {
                                console.error('Error deleting report:', error);
                              }
                            }
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certificates Section */}
      <div className={styles.tableSection}>
        <div className={styles.tablePanelHeader}>
          <div className={styles.tablePanelTitle}>Certificates</div>
        </div>

        {/* Certificate Filters */}
        <div className={styles.certificateFilters}>
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>Certificate</span>
            {certificateTypes.map((type) => (
              <label key={type} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="certificateType"
                  checked={certificateTypeFilter === type}
                  onChange={() => handleCertificateTypeFilter(type)}
                />
                {type}
              </label>
            ))}
          </div>
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>Status</span>
            {certificateStatuses.map((status) => (
              <label key={status} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="certificateStatus"
                  checked={certificateStatusFilter === status}
                  onChange={() => handleCertificateStatusFilter(status)}
                />
                {status}
              </label>
            ))}
          </div>
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>Property Type</span>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="propertyType"
                checked={certificatePropertyTypeFilter === 'All'}
                onChange={() => { setCertificatePropertyTypeFilter('All'); setCertificatesPage(1); }}
              />
              All
            </label>
            <label className={styles.radioLabel}>
              <input
                type="radio"
                name="propertyType"
                checked={certificatePropertyTypeFilter === 'Active only'}
                onChange={() => { setCertificatePropertyTypeFilter('Active only'); setCertificatesPage(1); }}
              />
              Active only
            </label>
          </div>
        </div>

        {certificates.length > 0 ? (
            <>
              <div className={styles.tableControls}>
                <div className={styles.tableControlsLeft}>
                  <span>Show</span>
                  <select
                    className={styles.tableSelect}
                    value={certificatesPerPage}
                    onChange={(e) => {
                      setCertificatesPerPage(Number(e.target.value));
                      setCertificatesPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
                <div className={styles.tableControlsRight}>
                  <input
                    type="text"
                    className={styles.tableSearch}
                    value={certificatesSearch}
                    onChange={(e) => handleCertificatesSearch(e.target.value)}
                    placeholder="Search certificates..."
                  />
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Completed</th>
                      <th>Expiration</th>
                      <th>Contractor Appointment</th>
                      <th>Property</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCertificates.map((cert) => (
                      <tr key={cert.id}>
                        <td>{cert.type || 'N/A'}</td>
                        <td>{cert.completed ? new Date(cert.completed).toLocaleDateString('en-GB') : '-'}</td>
                        <td>{cert.expiration ? new Date(cert.expiration).toLocaleDateString('en-GB') : '-'}</td>
                        <td>{cert.contractor_appointment ? new Date(cert.contractor_appointment).toLocaleDateString('en-GB') : '-'}</td>
                        <td>
                          {cert.property_id ? (
                            <Link to={`/properties/${cert.property_id}`} className={styles.propertyLink}>
                              {cert.property_name || 'N/A'}
                            </Link>
                          ) : (
                            cert.property_name || 'N/A'
                          )}
                        </td>
                        <td className={styles.actionButtons}>
                          <button
                            className={styles.btnDownload}
                            onClick={async () => {
                              try {
                                const response = await fetch(
                                  `${process.env.REACT_APP_API_URL}/api/certificates/${cert.id}/download`,
                                  {
                                    headers: {
                                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                                    },
                                  }
                                );
                                const data = await response.json();
                                if (data.success && data.downloadUrl) {
                                  window.open(data.downloadUrl, '_blank');
                                } else {
                                  alert('Unable to download certificate: ' + (data.message || 'Unknown error'));
                                }
                              } catch (error) {
                                console.error('Download error:', error);
                                alert('Error downloading certificate');
                              }
                            }}
                          >
                            Download
                          </button>
                          <Link to={`/properties/${cert.property_id}/certificate/${cert.id}/edit`} className={styles.btnEdit}>
                            Edit
                          </Link>
                          <button
                            className={cert.status === 'archived' ? styles.btnActive : styles.btnArchive}
                            onClick={async () => {
                              const endpoint = cert.status === 'archived' ? 'active' : 'archive';
                              try {
                                await fetch(`${process.env.REACT_APP_API_URL}/api/certificates/${cert.id}/${endpoint}`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                                  },
                                });
                                fetchClientData();
                              } catch (error) {
                                console.error('Status update error:', error);
                              }
                            }}
                          >
                            {cert.status === 'archived' ? 'Active' : 'Archive'}
                          </button>
                          <button
                            className={styles.btnRemove}
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to remove this certificate?')) {
                                try {
                                  await fetch(`${process.env.REACT_APP_API_URL}/api/certificates/${cert.property_id}/certificate/${cert.id}`, {
                                    method: 'DELETE',
                                    headers: {
                                      Authorization: `Bearer ${localStorage.getItem('token')}`,
                                    },
                                  });
                                  fetchClientData();
                                } catch (error) {
                                  console.error('Delete error:', error);
                                }
                              }
                            }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.tablePagination}>
                <div className={styles.paginationInfo}>
                  Showing {filteredCertificates.length === 0 ? 0 : ((certificatesPage - 1) * certificatesPerPage) + 1} to {Math.min(certificatesPage * certificatesPerPage, filteredCertificates.length)} of {filteredCertificates.length} entries
                  {certificatesSearch && ` (filtered from ${certificates.length} total entries)`}
                </div>
                <ul className={styles.pagination}>
                  <li className={certificatesPage === 1 ? styles.disabled : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); if (certificatesPage > 1) setCertificatesPage(p => p - 1); }}>
                      Previous
                    </a>
                  </li>
                  {(() => {
                    const pages = [];
                    const maxPages = 5;
                    let startPage = Math.max(1, certificatesPage - 2);
                    let endPage = Math.min(totalCertificatesPages, startPage + maxPages - 1);
                    if (endPage - startPage < maxPages - 1) {
                      startPage = Math.max(1, endPage - maxPages + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <li key={1} className={certificatesPage === 1 ? styles.active : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setCertificatesPage(1); }}>1</a>
                        </li>
                      );
                      if (startPage > 2) {
                        pages.push(<li key="ellipsis-start" className={styles.disabled}><span>...</span></li>);
                      }
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <li key={i} className={certificatesPage === i ? styles.active : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setCertificatesPage(i); }}>{i}</a>
                        </li>
                      );
                    }

                    if (endPage < totalCertificatesPages) {
                      if (endPage < totalCertificatesPages - 1) {
                        pages.push(<li key="ellipsis-end" className={styles.disabled}><span>...</span></li>);
                      }
                      pages.push(
                        <li key={totalCertificatesPages} className={certificatesPage === totalCertificatesPages ? styles.active : ''}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setCertificatesPage(totalCertificatesPages); }}>{totalCertificatesPages}</a>
                        </li>
                      );
                    }
                    return pages;
                  })()}
                  <li className={certificatesPage === totalCertificatesPages || totalCertificatesPages === 0 ? styles.disabled : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); if (certificatesPage < totalCertificatesPages) setCertificatesPage(p => p + 1); }}>
                      Next
                    </a>
                  </li>
                </ul>
              </div>
            </>
          ) : (
            <p className={styles.noData}>No certificate!</p>
          )}
      </div>

      {/* Modals */}
      {showAddContactModal && (
        <AddClientContactModal
          clientId={id}
          onSave={() => {
            setShowAddContactModal(false);
            fetchClientData();
          }}
          onClose={() => setShowAddContactModal(false)}
        />
      )}

      {showManageContactsModal && (
        <ManageClientContactsModal
          clientId={id}
          contacts={clientContacts}
          onSave={() => {
            setShowManageContactsModal(false);
            fetchClientData();
          }}
          onClose={() => setShowManageContactsModal(false)}
        />
      )}
    </div>
  );
};

export default ClientShow; 